import React from 'react';
import { Link, makeStyles } from '@material-ui/core';
import { ReportEntityType } from '../../types';
import { ENTITY_TYPE_OPTIONS_MAP } from '../../utils/report-param-utils';
import useReportQueryParams from '../../hooks/use-report-query-params';
import useStore from 'store';

const useStyles = makeStyles(() => {
  return {
    link: {
      cursor: 'pointer'
    }
  };
});

interface ReportLinkProps {
  entityId?: number;
  organizationId?: number;
  entityType?: ReportEntityType;
  children: React.ReactNode;
}

const ReportLink: React.FC<ReportLinkProps> = ({
  entityId,
  organizationId,
  entityType,
  children
}) => {
  const classes = useStyles();
  const [{ productFamilyCode }, setQuery] = useReportQueryParams();
  const [{ userPrivilegesCache }] = useStore();

  const hasPrivilege = (): boolean => {
    if (!entityType || !entityId) return false;

    const entityTypeOptions = ENTITY_TYPE_OPTIONS_MAP[entityType];
    const requiredClassroomPrivilege =
      entityTypeOptions.requiredClassroomPrivilege?.[productFamilyCode];
    const requiredOrgPrivilege = entityTypeOptions.requiredOrgPrivilege[productFamilyCode];

    switch (entityType) {
      case ReportEntityType.CLASSROOM:
        return (
          (!!requiredClassroomPrivilege &&
            userPrivilegesCache
              .getPrivilegedClassroomIds(requiredClassroomPrivilege)
              .includes(entityId)) ||
          (!!requiredOrgPrivilege &&
            !!organizationId &&
            userPrivilegesCache.getPrivilegedOrgIds(requiredOrgPrivilege).includes(organizationId))
        );
      case ReportEntityType.STUDENT:
      case ReportEntityType.USER:
        return true;
      case ReportEntityType.DISTRICT:
      case ReportEntityType.SCHOOL:
      case ReportEntityType.USER_GROUP:
        return (
          !!requiredOrgPrivilege &&
          userPrivilegesCache.getPrivilegedOrgIds(requiredOrgPrivilege).includes(entityId)
        );
      default:
        return false;
    }
  };

  const handleReportLinkClick = (): void => {
    setQuery({ entityType, entityId }, 'pushIn');
  };

  if (hasPrivilege()) {
    return (
      <Link color="secondary" className={classes.link} onClick={handleReportLinkClick}>
        {children}
      </Link>
    );
  }

  return <span>{children}</span>;
};

export default ReportLink;
